import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { BaseTextFieldProps, InputBaseComponentProps, InputProps } from "@mui/material";
import { green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import { alpha, styled } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Percent, { formatPercent, percentageValue } from "_shared/percentages/percent";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import React, { forwardRef, JSX, useCallback, useEffect } from "react";
import { NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format";
import FieldUndoButton from "work/proposals/_views/_buttons/field-undo-button";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import DiffText from "work/proposals/redlining/__diff/_views/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import {
  editableRedlineDisplayProps,
  ESCAPE_HINT,
  focusNextField,
  hasProposedChange,
  keepFocusOn,
  placeCaretAtEnd,
} from "work/proposals/redlining/field-redlines/_views/redline-field-focus";

/** The helper text of a required percentage left at zero, while it keeps the focus. */
export const PERCENT_REQUIRED_MESSAGE = `Enter a percentage, ${ESCAPE_HINT}.`;
const PERCENT_SUFFIX = "%";

const RedlineTextBox = styled(TextField)(() => ({
  minWidth: "20rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
  width: "min-content",
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TextContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
}));
const InputContainer = styled("span")(() => ({
  "&.MuiInputBase-input": {
    display: "flex",
    flexDirection: "column",
    height: "unset",
    "&.Mui-disabled": {
      webkitTextFillColor: "revert",
      opacity: "0.75",
    },
  },
}));

interface InputSubComponentProps extends InputBaseComponentProps {
  originalPercentFieldRedline: FieldRedline<Percent>;
  hideAcceptRejectButtons: boolean;
  hideUndoButton: boolean;
  focused: boolean;
  active?: boolean;
  animateAcceptedResolved?: boolean;
  onAcceptedResolvedAnimationStart?: () => void;
  onTraverseIn?: () => void;
  onPercentFieldRedlineChange?: (
    newRedline: FieldRedline<Percent>,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onActionButtonsChange?: (actionButtons: JSX.Element) => void;
  /** Reports the error a value that cannot be committed has, and undefined once it has none. */
  onDraftErrorChange?: (error: string | undefined) => void;
  negotiationId?: string;
  viewerScope?: string;
}

const InputSubComponent = forwardRef<HTMLInputElement, InputSubComponentProps>(function InputSubComponent(
  props,
  forwardedRef
) {
  const {
    className,
    originalPercentFieldRedline,
    hideAcceptRejectButtons,
    hideUndoButton,
    focused,
    required,
    animateAcceptedResolved,
    onAcceptedResolvedAnimationStart,
    onTraverseIn,
    onPercentFieldRedlineChange,
    onActionButtonsChange,
    onDraftErrorChange,
    onFocus,
    onBlur,
    readOnly,
    negotiationId,
    viewerScope,
  } = props;

  const [localPercentState, setLocalPercentState] = React.useState(() => ({
    sourceRedline: originalPercentFieldRedline,
    currentRedline: originalPercentFieldRedline,
    currentPercentNumeral:
      originalPercentFieldRedline.currentEntry?.value ?? originalPercentFieldRedline.revisedEntry?.value ?? 0,
  }));
  const [showPercentInput, setShowPercentInput] = React.useState<boolean>(false);
  // Set by Escape, which closes the input itself: the blur that follows must not commit what it reverted.
  const revertingRef = React.useRef(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleInputRef = useForkRef(forwardedRef, inputRef);

  // The input takes the focus the moment it opens, in place of the display that had it.
  useEffect(() => {
    if (showPercentInput) inputRef.current?.focus();
  }, [showPercentInput]);
  const lastCommittedRedlineRef = React.useRef<FieldRedline<Percent> | undefined>(undefined);
  // The redline as it stood the moment this edit session opened — what Escape/Cancel back out to.
  // Reading `originalPercentFieldRedline` for that instead would be wrong once a keystroke has already
  // propagated: the prop round-trips to reflect whatever was last sent, so by the time Escape is
  // pressed it may already equal the very edit being cancelled rather than the value it started from.
  const editSessionBaselineRedlineRef = React.useRef<FieldRedline<Percent>>(originalPercentFieldRedline);

  const currentRedline = localPercentState.sourceRedline === originalPercentFieldRedline
    ? localPercentState.currentRedline
    : originalPercentFieldRedline;
  const currentPercentNumeral = localPercentState.sourceRedline === originalPercentFieldRedline
    ? localPercentState.currentPercentNumeral
    : (originalPercentFieldRedline.currentEntry?.value ?? originalPercentFieldRedline.revisedEntry?.value ?? 0);

  const getEditablePercentValue = useCallback(
    () => originalPercentFieldRedline.currentEntry?.value ?? originalPercentFieldRedline.revisedEntry?.value ?? 0,
    [originalPercentFieldRedline]
  );

  const openPercentEditor = useCallback(() => {
    onTraverseIn?.();
    if (readOnly) return;
    revertingRef.current = false;
    editSessionBaselineRedlineRef.current = originalPercentFieldRedline;
    setLocalPercentState({
      sourceRedline: originalPercentFieldRedline,
      currentRedline: originalPercentFieldRedline,
      currentPercentNumeral: getEditablePercentValue(),
    });
    setShowPercentInput(true);
  }, [getEditablePercentValue, onTraverseIn, originalPercentFieldRedline, readOnly]);

  const clampValue = useCallback((): number => {
    if (!currentPercentNumeral || (currentPercentNumeral <= 1 && currentPercentNumeral >= 0))
      return currentPercentNumeral;
    let newNumber = currentPercentNumeral;
    // Clamp to between 0 and 100%
    if (newNumber < 0) newNumber = 0;
    if (newNumber > 1) newNumber = 1;
    setLocalPercentState((current) => ({
      ...current,
      sourceRedline: originalPercentFieldRedline,
      currentPercentNumeral: newNumber,
    }));
    return newNumber;
  }, [currentPercentNumeral, originalPercentFieldRedline]);

  // Parameterized on an explicit numeral (rather than closing over `currentPercentNumeral`) so
  // `onValueChange` below can ask "is the value just typed, this keystroke, savable?" before the state
  // update that would carry it into `currentPercentNumeral` has even been committed by React.
  const currentPercentDraftHasChanges = useCallback(
    (numeral: number): boolean => {
      let isChanged =
        numeral !==
        (originalPercentFieldRedline?.currentEntry?.value ?? originalPercentFieldRedline?.revisedEntry?.value ?? 0);
      if (currentRedline.isResolved) {
        isChanged = numeral !== originalPercentFieldRedline.currentEntry?.value;
      }

      return isChanged;
    },
    [currentRedline.isResolved, originalPercentFieldRedline]
  );

  const currentPercentDraftIsSavable = useCallback(
    (numeral: number): boolean => currentPercentDraftHasChanges(numeral) && !(required && numeral === 0),
    [currentPercentDraftHasChanges, required]
  );

  // The safety net for blur/Enter/Tab. In the common case the typed value has already reached
  // `onPercentFieldRedlineChange` from `onValueChange` below, and `lastCommittedRedlineRef` dedups this
  // call away; this still runs to close the editor, and to catch a draft that for some reason never
  // made it out of `onValueChange` (a `required` field crossing back over 0, for instance).
  const commitCurrentPercentDraft = useCallback((): void => {
    if (!showPercentInput || readOnly) return;
    if (!currentPercentDraftHasChanges(currentPercentNumeral)) {
      setShowPercentInput(false);
      return;
    }
    if (!currentPercentDraftIsSavable(currentPercentNumeral)) return;

    const newRedline = currentRedline.edit(new Percent(clampValue() ?? 0));
    if (!lastCommittedRedlineRef.current?.isEqualTo(newRedline)) {
      lastCommittedRedlineRef.current = newRedline;
      setLocalPercentState({
        sourceRedline: originalPercentFieldRedline,
        currentRedline: newRedline,
        currentPercentNumeral: newRedline.currentEntry?.value ?? 0,
      });
      onPercentFieldRedlineChange?.(newRedline, currentRedline.field);
    }
    setShowPercentInput(false);
  }, [
    clampValue,
    currentPercentDraftHasChanges,
    currentPercentDraftIsSavable,
    currentPercentNumeral,
    currentRedline,
    onPercentFieldRedlineChange,
    originalPercentFieldRedline,
    readOnly,
    showPercentInput,
  ]);

  // Escape and the Cancel button both back out of an in-progress edit. Typing now reaches
  // Save/Submit immediately (see `onValueChange` below) instead of waiting for a commit a disabled
  // Save button could never trigger (#4956) — so backing out has to actively undo whatever was already
  // sent, not merely reset what is on screen; before that change nothing had left the component yet, so
  // a local reset was enough on its own.
  const discardCurrentPercentDraft = useCallback((): void => {
    const baseline = editSessionBaselineRedlineRef.current;
    if (lastCommittedRedlineRef.current && !lastCommittedRedlineRef.current.isEqualTo(baseline)) {
      onPercentFieldRedlineChange?.(baseline, currentRedline.field);
    }
    lastCommittedRedlineRef.current = undefined;
    setLocalPercentState({
      sourceRedline: originalPercentFieldRedline,
      currentRedline: baseline,
      currentPercentNumeral: baseline.currentEntry?.value ?? baseline.revisedEntry?.value ?? 0,
    });
    setShowPercentInput(false);
  }, [currentRedline, onPercentFieldRedlineChange, originalPercentFieldRedline]);

  const renderAcceptRejectButtons = React.useCallback(() => {
    return (
      <>
        <Tooltip title="Accept Change">
          <span>
            <ActionButton
              onClick={() => {
                try {
                  const newRedline = originalPercentFieldRedline.accept();
                  setLocalPercentState({
                    sourceRedline: originalPercentFieldRedline,
                    currentRedline: newRedline,
                    currentPercentNumeral: newRedline.currentEntry?.value ?? 0,
                  });
                  onAcceptedResolvedAnimationStart?.();
                  onPercentFieldRedlineChange?.(newRedline);
                } catch (error) {
                  console.error("Error accepting change", error);
                  showErrorSnackbar(error, { doing: "accepting the change", operation: "proposal.redline.accept" });
                }
              }}
            >
              <CheckIcon color="success" />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Reject Change">
          <span>
            <ActionButton
              onClick={() => {
                try {
                  const newRedline = originalPercentFieldRedline.reject();
                  setLocalPercentState({
                    sourceRedline: originalPercentFieldRedline,
                    currentRedline: newRedline,
                    currentPercentNumeral: newRedline.currentEntry?.value ?? 0,
                  });
                  onPercentFieldRedlineChange?.(newRedline);
                } catch (error) {
                  console.error("Error rejecting change", error);
                  showErrorSnackbar(error, { doing: "rejecting the change", operation: "proposal.redline.reject" });
                }
              }}
            >
              <CloseIcon color="error" />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [originalPercentFieldRedline, onPercentFieldRedlineChange, onAcceptedResolvedAnimationStart]);

  const renderUndoButton = React.useCallback(() => {
    return (
      <FieldUndoButton
        onUndo={() => {
        try {
          const newRedline = originalPercentFieldRedline.undo();
          setLocalPercentState({
            sourceRedline: originalPercentFieldRedline,
            currentRedline: newRedline,
            currentPercentNumeral: newRedline.currentEntry?.value ?? 0,
          });
          onPercentFieldRedlineChange?.(newRedline, {
            name: newRedline.field,
            id: newRedline.fieldId,
          });
        } catch (error) {
          console.error("Error undoing changes", error);
          showErrorSnackbar(error, { doing: "undoing the changes", operation: "proposal.redline.undo" });
        }
        }}
      />
    );
  }, [originalPercentFieldRedline, onPercentFieldRedlineChange]);

  
  const renderActionButtons = React.useCallback(() => {
    return (
      <>
        {!showPercentInput && !readOnly && (
          <>
            {!hideAcceptRejectButtons && !originalPercentFieldRedline.isResolved && renderAcceptRejectButtons()}
            {originalPercentFieldRedline.canBeUndone && !hideUndoButton && renderUndoButton()}
          </>
        )}
        <ProposalFieldHistoryButton
          field={currentRedline.field}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
        />
      </>
    );
  }, [
    showPercentInput,
    readOnly,
    hideAcceptRejectButtons,
    originalPercentFieldRedline.isResolved,
    originalPercentFieldRedline.canBeUndone,
    hideUndoButton,
    negotiationId,
    viewerScope,
    currentRedline.field,
    renderAcceptRejectButtons,
    renderUndoButton,
  ]);

  useEffect(() => {
    onActionButtonsChange?.(renderActionButtons());
  }, [showPercentInput, originalPercentFieldRedline, currentRedline, onActionButtonsChange, renderActionButtons]);

  const resolvedTextChanges = React.useMemo(() => {
    if (!originalPercentFieldRedline.isResolved || !originalPercentFieldRedline.textChanges?.length) return undefined;
    return originalPercentFieldRedline.textChanges;
  }, [originalPercentFieldRedline]);
  const hasAcceptedResolvedChange =
    originalPercentFieldRedline.isResolved &&
    originalPercentFieldRedline.isAccepted &&
    originalPercentFieldRedline.wasRevised;
  const showAcceptedResolvedAnimation = Boolean(
    animateAcceptedResolved &&
    hasAcceptedResolvedChange &&
    resolvedTextChanges
  );
  const showResolvedDiff = Boolean(
    resolvedTextChanges &&
    (!hasAcceptedResolvedChange || showAcceptedResolvedAnimation)
  );
  const showResolvedCurrentValue = Boolean(
    originalPercentFieldRedline.isResolved &&
    (!resolvedTextChanges || (hasAcceptedResolvedChange && !showAcceptedResolvedAnimation))
  );

  // A required percentage cannot be committed at zero.
  function percentDraftIsInvalid(numeral: number): boolean {
    return currentPercentDraftHasChanges(numeral) && !currentPercentDraftIsSavable(numeral);
  }

  // Leaving the field commits it. A value that cannot be committed keeps the focus instead, with its
  // error, until it is one that can or Escape reverts it.
  function handlePercentInputBlur(event: React.FocusEvent<HTMLInputElement>) {
    onBlur?.(event);
    if (revertingRef.current) return;
    if (percentDraftIsInvalid(currentPercentNumeral)) {
      onDraftErrorChange?.(PERCENT_REQUIRED_MESSAGE);
      keepFocusOn(event.currentTarget);
      return;
    }
    onDraftErrorChange?.(undefined);
    try {
      commitCurrentPercentDraft();
    } catch (error) {
      console.error("Error saving changes", error);
      showErrorSnackbar(error, { doing: "saving the change", operation: "proposal.redline.edit" });
    }
  }

  function handlePercentInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    event.stopPropagation();
    if (event.key === KeyboardKey.Escape) {
      revertingRef.current = true;
      onDraftErrorChange?.(undefined);
      discardCurrentPercentDraft();
      event.currentTarget.blur();
      return;
    }
    if (event.key !== KeyboardKey.Enter) return;
    event.preventDefault();
    if (percentDraftIsInvalid(currentPercentNumeral)) {
      onDraftErrorChange?.(PERCENT_REQUIRED_MESSAGE);
      return;
    }
    // Moving on is what commits: the input's blur does it.
    focusNextField(event.currentTarget);
  }

  function handleDisplayFocus(event: React.FocusEvent<HTMLElement>) {
    onFocus?.(event as React.FocusEvent<HTMLInputElement>);
    openPercentEditor();
  }

  return (
    <TextContainer>
      <InputContainer
        className={className}
        // The redline is the field's display, and it takes the focus the way an input does: focusing it
        // opens the input in its place (#6624). A read-only field has nothing to open.
        ref={showPercentInput || readOnly ? undefined : (forwardedRef as React.Ref<HTMLSpanElement>)}
        {...(!showPercentInput && !readOnly &&
          editableRedlineDisplayProps(
            originalPercentFieldRedline.label,
            hasProposedChange(originalPercentFieldRedline.textChanges),
          ))}
        onFocus={showPercentInput || readOnly ? undefined : handleDisplayFocus}
      >
        {!showPercentInput && showResolvedDiff && resolvedTextChanges && (
          <DiffText
            textChanges={resolvedTextChanges}
            allResolved={true}
            acceptedResolved={hasAcceptedResolvedChange}
            animateAcceptedResolved={animateAcceptedResolved}
          />
        )}
        {!showPercentInput && showResolvedCurrentValue && (
          <Typography>
            {originalPercentFieldRedline.currentEntry?.toString() ??
              originalPercentFieldRedline.revisedEntry?.toString() ??
              formatPercent(0)}
          </Typography>
        )}
        {!showPercentInput && !originalPercentFieldRedline.isResolved && originalPercentFieldRedline.textChanges.length > 0 && (
          <DiffText
            textChanges={originalPercentFieldRedline.textChanges}
            allResolved={originalPercentFieldRedline.isResolved}
          />
        )}
        {!showPercentInput && !originalPercentFieldRedline.isResolved && originalPercentFieldRedline.textChanges.length < 1 && (
          <Typography>
            {originalPercentFieldRedline.currentEntry?.toString() ??
              originalPercentFieldRedline.revisedEntry?.toString() ??
              formatPercent(0)}
          </Typography>
        )}
        {showPercentInput && (
        <NumericFormat
          inputRef={handleInputRef}
          focused={focused}
          placeholder="Enter new percentage"
          variant="standard"
          style={{ width: "100%" }}
          customInput={TextField}
          value={currentPercentNumeral ? percentageValue(currentPercentNumeral).toFixed(2) : 0}
          suffix={PERCENT_SUFFIX}
          type="text"
          thousandSeparator={false}
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale={true}
          isAllowed={(values) => {
            const { floatValue } = values;
            return floatValue == null || (floatValue >= 0 && floatValue <= 100);
          }}
          onKeyDown={handlePercentInputKeyDown}
          onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) => {
            try {
              if (sourceInfo.source === "prop") return;
              let updatedDiscount = Number.parseFloat(
                (Math.floor((values.floatValue ?? 0) * 1000) * 0.00001).toPrecision(4)
              );
              let newRedline = currentRedline.edit(new Percent(updatedDiscount));
              setLocalPercentState({
                sourceRedline: originalPercentFieldRedline,
                currentRedline: newRedline,
                currentPercentNumeral: updatedDiscount,
              });
              onActionButtonsChange?.(renderActionButtons());
              if (!percentDraftIsInvalid(updatedDiscount) && onDraftErrorChange) onDraftErrorChange(undefined);

              // Reach Save/Submit as soon as a valid discount is typed, rather than waiting for the
              // field to lose focus: a disabled Save button never receives the mousedown that would
              // otherwise blur it and trigger `commitCurrentPercentDraft`, so nothing short of
              // clicking elsewhere first could ever unlock it (#4956).
              if (
                !readOnly &&
                currentPercentDraftIsSavable(updatedDiscount) &&
                !lastCommittedRedlineRef.current?.isEqualTo(newRedline)
              ) {
                try {
                  lastCommittedRedlineRef.current = newRedline;
                  onPercentFieldRedlineChange?.(newRedline, currentRedline.field);
                } catch (error) {
                  console.error("Error saving changes", error);
                  showErrorSnackbar(error, { doing: "saving the change", operation: "proposal.redline.edit" });
                }
              }
            } catch (error) {
              console.error("Error changing value", error);
              showErrorSnackbar(error, { doing: "changing the value", operation: "proposal.redline.edit" });
            }
          }}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
            placeCaretAtEnd(event.currentTarget, PERCENT_SUFFIX.length);
            onFocus?.(event);
          }}
          onBlur={handlePercentInputBlur}
        />
        )}
      </InputContainer>
    </TextContainer>
  );
});

export interface PercentRedlineInputProps extends BaseTextFieldProps {
  focused: boolean;
  active?: boolean;
  lockAdornment?: React.ReactNode;
  originalPercentFieldRedline: FieldRedline<Percent>;
  showPercentInput?: boolean;
  readOnly?: boolean;
  hideUndoButton?: boolean;
  hideAcceptRejectButtons?: boolean;
  onTraverseIn?: () => void;
  onPercentFieldRedlineChange?: (
    newRedline: FieldRedline<Percent>,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  negotiationId?: string;
  viewerScope?: string;
}

const PercentRedlineInput = (props: Readonly<PercentRedlineInputProps>) => {
  const {
    focused,
    originalPercentFieldRedline,
    hideUndoButton,
    hideAcceptRejectButtons,
    lockAdornment,
    readOnly,
    variant,
    margin,
    onPercentFieldRedlineChange,
    onTraverseIn,
    onFocus,
    onBlur,
    negotiationId,
    viewerScope,
    ...otherProps
  } = props;

  const [actionButtons, setActionButtons] = React.useState<JSX.Element>();
  const [draftError, setDraftError] = React.useState<string>();
  const hasAcceptedResolvedChange =
    originalPercentFieldRedline.isResolved &&
    originalPercentFieldRedline.isAccepted &&
    originalPercentFieldRedline.wasRevised;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  const handleActionButtonsChange = useCallback((actionButtons: JSX.Element) => {
    setActionButtons(prev => (prev === actionButtons ? prev : actionButtons));
  }, []);

  function getInputProps(variant: string | undefined): Partial<InputProps> {
    const inputProps: Partial<InputProps> = {
      inputComponent: InputSubComponent,
      inputProps: {
        readOnly,
        hideUndoButton,
        hideAcceptRejectButtons,
        originalPercentFieldRedline,
        focused,
        animateAcceptedResolved,
        onAcceptedResolvedAnimationStart: triggerAcceptedResolvedAnimation,
        onTraverseIn,
        onPercentFieldRedlineChange,
        negotiationId,
        viewerScope,
        onActionButtonsChange: handleActionButtonsChange,
        onDraftErrorChange: setDraftError,
        onFocus,
        onBlur,
      },
      startAdornment: lockAdornment,
      endAdornment: actionButtons,
    };
    if (variant && variant !== "outlined") {
      inputProps.disableUnderline = true;
    }

    return inputProps;
  }

  return (
    <RedlineTextBox
      {...otherProps}
      focused={focused}
      label={originalPercentFieldRedline.label}
      error={otherProps.error || Boolean(draftError)}
      helperText={draftError ?? (hasAcceptedResolvedChange ? "Resolved" : otherProps.helperText)}
      variant={variant ?? "outlined"}
      margin={margin ?? "normal"}
      sx={{
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(green[500], 0.06),
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: green[500],
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: green[600],
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
        }),
        ...(lockAdornment && {
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
            color: "warning.main",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
            color: "warning.main",
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "warning.main",
          },
        }),
      }}
      slotProps={{
        input: getInputProps(variant),
        inputLabel: { shrink: true },
      }}
      onFocus={onTraverseIn}
    />
  );
};

export default PercentRedlineInput;
